/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { Ticket } from '@touchplan/common';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
import { TimelineTicketList } from '../../../common/fancy-firebase-ticket/lists/TimelineTicketList';
import { Point } from 'pixi.js-legacy';
import { Observable, Subject } from 'rxjs';
/**
 * Interface to control the visibility and data of the overflow timeline ticket grouped visual
 */
export interface ITimelineTicketOverflow {
	screenPosition: Point;
	tickets: Ticket[];
	visible: boolean;
}
/**
 * TimelineTicketService
 * Adds the TimelineTicketService so the canvas code can access the timeline tickets list outside of Angular-land.
 * We already have a TimelineTicketList using the “modern” Firebase wrappers, so this passes that into an @Injectable()
 * so we can pass that data around without injecting the entire PlanState object needlessly
 */
@Injectable({ providedIn: 'root' })
export class TimelineTicketService extends AbstractCanvasConnection<Map<string, Ticket>> {
	/**
	 * Private subject containing timeline ticket grouped overflow data. We manipulate this subject to emit data
	 * to the public facing observable stream.
	 */
	private _overflowChanged$: Subject<ITimelineTicketOverflow> = new Subject<ITimelineTicketOverflow>();
	/**
	 * Public observable containing timeline ticket grouped overflow data
	 */
	public overflow$: Observable<ITimelineTicketOverflow> = this._overflowChanged$.asObservable();
	/**
	 * Storing the last visibility state of the overflow UI here so we can skip `closeOverflow` calls if it is not currently open
	 * @private
	 */
	private _isOverflowVisible: boolean = false;
	/**
	 * Registers the service to receive the following data:
	 * 	- ticketList: The TimelineTicketList, which typically comes from PlanState.
	 * @param ticketList TimelineTicketList An instance of the TimelineTicketList containing timeline ticket data
	 */
	public registerPlanData(ticketList: TimelineTicketList): void {
		this.clearPreviousSubscriber();
		this._subscription = ticketList.list$.subscribe((data: Map<string, Ticket>) => this._changed$.next(data));
	}
	/**
	 * Used to open the Timeline Tickets group UI. This is the UI element that appears when you tap on a timeline ticket.
	 * @param point
	 * @param tickets
	 */
	public openOverflow(point: Point, tickets: Ticket[]): void {
		this._isOverflowVisible = true;
		this._overflowChanged$.next({
			tickets,
			screenPosition: point,
			visible: this._isOverflowVisible,
		});
	}
	/**
	 * Used to close the Timeline Tickets group UI. This is the UI element that appears when you tap on a timeline ticket.
	 */
	public closeOverflow(): void {
		if (!this._isOverflowVisible) {
			return;
		}
		this._isOverflowVisible = false;
		this._overflowChanged$.next({
			tickets: [],
			visible: this._isOverflowVisible,
			screenPosition: null,
		});
	}
}
